<template>
  <div class="admin-container">
    <AdminSidebar :isOpen="isSidebarOpen" :userData="currentUser" />

    <!-- Main Content -->
    <div class="main-content" :class="{ 'content-shifted': isSidebarOpen }">
      <nav class="admin-nav">
        <button class="menu-btn" @click="toggleSidebar">
          <i class="fas fa-bars"></i>
        </button>
      </nav>

      <div class="admin-content">
        <div class="blocked-phones-container">
          <div class="header">
            <h1>Quản lý số điện thoại bị chặn</h1>
            <button class="add-btn" @click="openAddModal">
              <i class="fas fa-plus"></i> Thêm số điện thoại
            </button>
          </div>

          <!-- Filters -->
          <div class="filters">
            <div class="search-box">
              <i class="fas fa-search"></i>
              <input
                type="text"
                v-model="searchQuery"
                @input="handleSearch"
                placeholder="Tìm kiếm số điện thoại..."
              />
            </div>
            <div class="filter-buttons">
              <button
                class="filter-btn"
                :class="{ active: showActive }"
                @click="toggleActiveFilter(true)"
              >
                <i class="fas fa-ban"></i> Đang chặn
              </button>
              <button
                class="filter-btn"
                :class="{ active: !showActive }"
                @click="toggleActiveFilter(false)"
              >
                <i class="fas fa-check-circle"></i> Đã gỡ chặn
              </button>
            </div>
          </div>


          <!-- Table -->
          <div class="table-container">
            <!-- Loading State -->
            <div v-if="isLoading" class="table-loading">
              <div class="table-spinner"></div>
              <p>Đang tải dữ liệu...</p>
            </div>

            <!-- Empty State -->
            <div v-else-if="filteredPhones.length === 0" class="table-empty">
              <i class="fas fa-phone-slash table-empty-icon"></i>
              <h3 class="table-empty-title">Không có số điện thoại nào</h3>
              <p class="table-empty-text">{{ showActive ? 'Chưa có số điện thoại nào bị chặn' : 'Không có số điện thoại nào đã gỡ chặn' }}</p>
              <button v-if="showActive" class="table-empty-action" @click="openAddModal">
                <i class="fas fa-plus"></i> Thêm số điện thoại
              </button>
            </div>

            <!-- Table Content -->
            <div v-else class="table-responsive has-mobile-cards">
              <!-- Desktop Table -->
              <table class="admin-table">
                <thead>
                  <tr>
                    <th>Số điện thoại</th>
                    <th>Lý do</th>
                    <th>Ngày chặn</th>
                    <th>Trạng thái</th>
                    <th class="col-actions">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="phone in paginatedPhones" :key="phone.id">
                    <td class="phone-cell nowrap">
                      <i class="fas fa-phone"></i>
                      {{ phone.phone_number }}
                    </td>
                    <td>
                      <span class="text-truncate" :title="phone.reason || 'Không có'">
                        {{ phone.reason || 'Không có' }}
                      </span>
                    </td>
                    <td class="nowrap">{{ formatDate(phone.blocked_at) }}</td>
                    <td>
                      <span :class="['status-badge', phone.is_active ? 'inactive' : 'active']">
                        {{ phone.is_active ? 'Đang chặn' : 'Đã gỡ' }}
                      </span>
                    </td>
                    <td class="col-actions">
                      <div class="action-buttons">
                        <button
                          v-if="phone.is_active"
                          class="action-btn view-btn"
                          @click="unblockPhone(phone)"
                          title="Gỡ chặn"
                          aria-label="Gỡ chặn số điện thoại"
                        >
                          <i class="fas fa-unlock"></i>
                        </button>
                        <button
                          v-else
                          class="action-btn delete-btn"
                          @click="reblockPhone(phone)"
                          title="Chặn lại"
                          aria-label="Chặn lại số điện thoại"
                        >
                          <i class="fas fa-ban"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Mobile Card View -->
              <div class="mobile-card-list">
                <div v-for="phone in paginatedPhones" :key="'card-' + phone.id" class="mobile-card">
                  <div class="mobile-card-header">
                    <div class="mobile-card-title">
                      <i class="fas fa-phone"></i> {{ phone.phone_number }}
                    </div>
                    <span :class="['status-badge', phone.is_active ? 'inactive' : 'active']">
                      {{ phone.is_active ? 'Đang chặn' : 'Đã gỡ' }}
                    </span>
                  </div>
                  <div class="mobile-card-body">
                    <div class="mobile-card-row">
                      <span class="mobile-card-label">Lý do:</span>
                      <span class="mobile-card-value">{{ phone.reason || 'Không có' }}</span>
                    </div>
                    <div class="mobile-card-row">
                      <span class="mobile-card-label">Ngày chặn:</span>
                      <span class="mobile-card-value">{{ formatDate(phone.blocked_at) }}</span>
                    </div>
                  </div>
                  <div class="mobile-card-actions">
                    <button
                      v-if="phone.is_active"
                      class="action-btn view-btn with-label"
                      @click="unblockPhone(phone)"
                    >
                      <i class="fas fa-unlock"></i> Gỡ chặn
                    </button>
                    <button
                      v-else
                      class="action-btn delete-btn with-label"
                      @click="reblockPhone(phone)"
                    >
                      <i class="fas fa-ban"></i> Chặn lại
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div class="pagination" v-if="filteredPhones.length > 0">
            <button
              class="pagination-btn"
              @click="changePage(currentPage - 1)"
              :disabled="currentPage === 1"
            >
              <i class="fas fa-chevron-left"></i> Trang trước
            </button>
            <div class="page-info">
              Trang {{ currentPage }} / {{ totalPages || 1 }}
            </div>
            <button
              class="pagination-btn"
              @click="changePage(currentPage + 1)"
              :disabled="currentPage >= totalPages"
            >
              Trang sau <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Modal -->
    <div class="modal" v-if="showModal" @click.self="closeModal">
      <div class="modal-content modal-sm">
        <div class="modal-header">
          <h2><i class="fas fa-plus-circle"></i> Thêm số điện thoại bị chặn</h2>
          <button class="close-btn" @click="closeModal">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveBlockedPhone">
            <div class="form-group">
              <label for="phone_number">Số điện thoại <span class="required">*</span></label>
              <div class="input-with-icon">
                <i class="fas fa-phone input-icon"></i>
                <input
                  type="text"
                  id="phone_number"
                  v-model="phoneForm.phone_number"
                  required
                  placeholder="VD: 0912345678"
                  pattern="[0-9]{10,11}"
                />
              </div>
            </div>
            <div class="form-group">
              <label for="reason">Lý do chặn</label>
              <div class="input-with-icon">
                <i class="fas fa-comment input-icon"></i>
                <input
                  type="text"
                  id="reason"
                  v-model="phoneForm.reason"
                  placeholder="VD: Đặt vé không thanh toán nhiều lần"
                  maxlength="255"
                />
              </div>
            </div>
            <div class="form-actions">
              <button type="button" class="cancel-btn" @click="closeModal">
                <i class="fas fa-times"></i> Hủy
              </button>
              <button type="submit" class="save-btn" :disabled="isSaving">
                <i class="fas fa-spinner fa-spin" v-if="isSaving"></i>
                <i class="fas fa-ban" v-else></i>
                {{ isSaving ? 'Đang lưu...' : 'Chặn số này' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from '@/plugins/axios'
import AdminSidebar from '../common/AdminSidebar.vue'
import { ToastService } from '@/services/ToastService'

const isProd = process.env.VUE_APP_IS_PROD === 'true'

const API_BASE_URL = isProd
  ? process.env.VUE_APP_BACKEND_URL_PROD
  : process.env.VUE_APP_BACKEND_URL_DEV

export default {
  name: 'AdminBlockedPhones',
  components: {
    AdminSidebar
  },
  data() {
    return {
      blockedPhones: [],
      isLoading: false,
      isSaving: false,
      showModal: false,
      searchQuery: '',
      showActive: true,
      isSidebarOpen: false,
      currentUser: {
        full_name: 'Admin User',
        role: 'Administrator'
      },
      phoneForm: {
        phone_number: '',
        reason: ''
      },
      currentPage: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    filteredPhones() {
      let filtered = this.blockedPhones

      // Filter by active status
      filtered = filtered.filter(phone => phone.is_active === this.showActive)

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(phone =>
          phone.phone_number.includes(query) ||
          (phone.reason && phone.reason.toLowerCase().includes(query))
        )
      }

      return filtered
    },
    totalPages() {
      return Math.ceil(this.filteredPhones.length / this.itemsPerPage)
    },
    paginatedPhones() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredPhones.slice(start, end)
    }
  },
  created() {
    this.fetchBlockedPhones()
    const sidebarState = localStorage.getItem('sidebarOpen')
    if (sidebarState !== null) {
      this.isSidebarOpen = sidebarState === 'true'
    }
  },
  methods: {
    async fetchBlockedPhones() {
      try {
        this.isLoading = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Get both active and inactive phones
        const [activeRes, inactiveRes] = await Promise.all([
          axiosInstance.get(`${API_BASE_URL}/admin/blocked-phones/`, {
            headers,
            params: { is_active: true }
          }),
          axiosInstance.get(`${API_BASE_URL}/admin/blocked-phones/`, {
            headers,
            params: { is_active: false }
          })
        ])

        this.blockedPhones = [...(activeRes.data || []), ...(inactiveRes.data || [])]
      } catch (error) {
        console.error('Error fetching blocked phones:', error)
        ToastService.error('Không thể tải danh sách số điện thoại bị chặn.')
      } finally {
        this.isLoading = false
      }
    },
    handleSearch() {
      this.currentPage = 1
    },
    toggleActiveFilter(showActive) {
      this.showActive = showActive
      this.currentPage = 1
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen
      localStorage.setItem('sidebarOpen', this.isSidebarOpen)
    },
    openAddModal() {
      this.phoneForm = {
        phone_number: '',
        reason: ''
      }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    async saveBlockedPhone() {
      try {
        this.isSaving = true
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        await axiosInstance.post(
          `${API_BASE_URL}/admin/blocked-phones/`,
          this.phoneForm,
          { headers }
        )

        ToastService.success(`Số ${this.phoneForm.phone_number} đã được thêm vào danh sách chặn`)
        await this.fetchBlockedPhones()
        this.closeModal()
      } catch (error) {
        console.error('Error blocking phone:', error)
        const errorMessage = error.response?.data?.detail || 'Đã xảy ra lỗi khi thêm số điện thoại'
        ToastService.error(errorMessage)
      } finally {
        this.isSaving = false
      }
    },
    async unblockPhone(phone) {
      if (!confirm(`Bạn có chắc muốn gỡ chặn số ${phone.phone_number}?`)) {
        return
      }

      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        await axiosInstance.delete(
          `${API_BASE_URL}/admin/blocked-phones/${phone.id}`,
          { headers }
        )

        ToastService.success(`Số ${phone.phone_number} đã được gỡ chặn`)
        await this.fetchBlockedPhones()
      } catch (error) {
        console.error('Error unblocking phone:', error)
        ToastService.error('Không thể gỡ chặn số điện thoại')
      }
    },
    async reblockPhone(phone) {
      if (!confirm(`Bạn có chắc muốn chặn lại số ${phone.phone_number}?`)) {
        return
      }

      try {
        const token = localStorage.getItem('adminToken')
        const headers = { Authorization: `Bearer ${token}` }

        // Re-add the phone to blocked list
        await axiosInstance.post(
          `${API_BASE_URL}/admin/blocked-phones/`,
          {
            phone_number: phone.phone_number,
            reason: phone.reason
          },
          { headers }
        )

        ToastService.success(`Số ${phone.phone_number} đã được chặn lại`)
        await this.fetchBlockedPhones()
      } catch (error) {
        console.error('Error reblocking phone:', error)
        ToastService.error('Không thể chặn lại số điện thoại')
      }
    },
    changePage(newPage) {
      this.currentPage = newPage
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/admin.scss';
@import '@/assets/styles/admin-table.scss';

.blocked-phones-container {
  padding: 1.5rem;
  background-color: transparent;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h1 {
    font-size: 1.875rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
  }
}

.add-btn {
  background: linear-gradient(to right, #dc2626, #ef4444);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 0.9375rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    background: linear-gradient(to right, #b91c1c, #dc2626);
    transform: translateY(-1px);
  }
}

.add-btn-sm {
  @extend .add-btn;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.search-box {
  position: relative;
  width: 100%;
  max-width: 400px;

  input {
    width: 100%;
    padding: 0.75rem 0.75rem 0.75rem 2.25rem;
    border-radius: 0.5rem;
    border: 1px solid #e2e8f0;
    font-size: 0.9375rem;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #ef4444;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.25);
    }
  }

  i {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
  }
}

.filter-buttons {
  display: flex;
  gap: 0.5rem;
}

.filter-btn {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &.active {
    background: #ef4444;
    color: white;
    border-color: #ef4444;
  }

  &:hover:not(.active) {
    background: #e2e8f0;
  }
}

.blocked-phones-table {
  background: white;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

table {
  width: 100%;
  border-collapse: collapse;

  thead {
    background: #f8fafc;
  }

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
  }

  th {
    font-weight: 600;
    color: #475569;
    font-size: 0.875rem;
  }
  
  /* Allow reason column to show full content */
  td:nth-child(2) {
    max-width: 300px;
    word-wrap: break-word;
    white-space: normal;
    line-height: 1.4;
  }
}

.phone-cell {
  font-weight: 600;
  color: #1e293b;

  i {
    color: #ef4444;
    margin-right: 0.5rem;
  }
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;

  &.active {
    background: #fef2f2;
    color: #dc2626;
  }

  &.inactive {
    background: #f0fdf4;
    color: #16a34a;
  }
}

.action-cell {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  padding: 0.5rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;

  &.deactivate-btn {
    background: #dcfce7;
    color: #16a34a;

    &:hover {
      background: #16a34a;
      color: white;
    }
  }

  &.activate-btn {
    background: #fef2f2;
    color: #dc2626;

    &:hover {
      background: #dc2626;
      color: white;
    }
  }
}

.no-data {
  text-align: center;
  padding: 3rem;
  color: #64748b;
  background: white;
  border-radius: 0.5rem;

  i {
    font-size: 3rem;
    margin-bottom: 1rem;
    color: #cbd5e1;
  }

  p {
    margin-bottom: 1rem;
  }
}

.loading {
  text-align: center;
  padding: 3rem;
  color: #64748b;

  i {
    margin-right: 0.5rem;
  }
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
}

.pagination-btn {
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f1f5f9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.page-info {
  color: #64748b;
  font-size: 0.875rem;
}

/* Modal styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 0.75rem;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.modal-sm {
  max-width: 450px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;

  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    i {
      color: #ef4444;
    }
  }
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: #64748b;
  cursor: pointer;
  padding: 0.25rem;

  &:hover {
    color: #1e293b;
  }
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.25rem;

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: #374151;
  }

  .required {
    color: #ef4444;
  }
}

.input-with-icon {
  position: relative;

  .input-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
  }

  input {
    width: 100%;
    padding: 0.75rem 0.75rem 0.75rem 2.5rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    font-size: 0.9375rem;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #ef4444;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.25);
    }
  }
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.cancel-btn {
  padding: 0.75rem 1.25rem;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.9375rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: #e2e8f0;
  }
}

.save-btn {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(to right, #dc2626, #ef4444);
  color: white;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.9375rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: linear-gradient(to right, #b91c1c, #dc2626);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
